/** @typedef {'dark' | 'light'} ThemePreference */

/** @typedef {'en' | 'hi'} LanguagePreference */

/** @typedef {'gps' | 'manual'} LocationSource */

/**
 * @typedef {Object} UserLocationPreference
 * @property {string | null} label
 * @property {number | null} latitude
 * @property {number | null} longitude
 * @property {LocationSource | null} source
 */

export const THEME_PREFERENCE_VALUES = /** @type {const} */ (['dark', 'light']);

export const DEFAULT_THEME_PREFERENCE = /** @type {ThemePreference} */ ('light');

export const LANGUAGE_PREFERENCE_VALUES = /** @type {const} */ (['en', 'hi']);

export const DEFAULT_LANGUAGE_PREFERENCE = /** @type {LanguagePreference} */ ('en');

export const LOCATION_SOURCE_VALUES = /** @type {const} */ (['gps', 'manual']);

/** @param {unknown} value @returns {value is ThemePreference} */
export function isThemePreference(value) {
  return THEME_PREFERENCE_VALUES.includes(value);
}

/** @param {unknown} value @returns {value is LocationSource} */
export function isLocationSource(value) {
  return LOCATION_SOURCE_VALUES.includes(value);
}

/** @param {unknown} value @returns {value is LanguagePreference} */
export function isLanguagePreference(value) {
  return LANGUAGE_PREFERENCE_VALUES.includes(value);
}

/** @param {{ themePreference?: string } | null | undefined} profile @returns {ThemePreference} */
export function resolveThemePreference(profile) {
  const raw = profile?.themePreference;
  return isThemePreference(raw) ? raw : DEFAULT_THEME_PREFERENCE;
}

/** @param {{ languagePreference?: string } | null | undefined} profile @returns {LanguagePreference} */
export function resolveLanguagePreference(profile) {
  const raw = profile?.languagePreference;
  return isLanguagePreference(raw) ? raw : DEFAULT_LANGUAGE_PREFERENCE;
}

/** @param {LanguagePreference} languagePreference */
export function languagePreferenceToShowEnglish(languagePreference) {
  return languagePreference === 'en';
}

/** @param {boolean} showEnglish @returns {LanguagePreference} */
export function showEnglishToLanguagePreference(showEnglish) {
  return showEnglish ? 'en' : 'hi';
}

/** @param {unknown} value @returns {value is number} */
function isFiniteCoord(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * @param {unknown} raw
 * @returns {UserLocationPreference | null}
 */
export function normalizeUserLocation(raw) {
  if (raw == null) return null;
  if (typeof raw !== 'object') return null;

  const label = typeof raw.label === 'string' ? raw.label.trim() : '';
  const latitude = isFiniteCoord(raw.latitude) ? raw.latitude : null;
  const longitude = isFiniteCoord(raw.longitude) ? raw.longitude : null;
  const source = isLocationSource(raw.source) ? raw.source : null;

  if (!label && latitude == null && longitude == null) return null;

  return {
    label: label || null,
    latitude,
    longitude,
    source,
  };
}

/**
 * @param {{ location_label?: string | null; location_lat?: number | null; location_lng?: number | null; location_source?: string | null } | null | undefined} row
 * @returns {UserLocationPreference | null}
 */
export function locationFromDbRow(row) {
  if (!row) return null;
  return normalizeUserLocation({
    label: row.location_label,
    latitude: row.location_lat,
    longitude: row.location_lng,
    source: row.location_source,
  });
}

/**
 * @param {UserLocationPreference | null | undefined} location
 * @returns {{ location_label: string | null; location_lat: number | null; location_lng: number | null; location_source: LocationSource | null }}
 */
export function locationToDbFields(location) {
  const normalized = normalizeUserLocation(location);
  if (!normalized) {
    return {
      location_label: null,
      location_lat: null,
      location_lng: null,
      location_source: null,
    };
  }

  return {
    location_label: normalized.label,
    location_lat: normalized.latitude,
    location_lng: normalized.longitude,
    location_source: normalized.source,
  };
}

/**
 * @param {unknown} patch
 * @returns {UserLocationPreference | null | undefined}
 */
export function validateLocationPatch(patch) {
  if (!Object.prototype.hasOwnProperty.call(patch, 'location')) return undefined;
  if (patch.location === null) return null;
  const normalized = normalizeUserLocation(patch.location);
  if (!normalized) {
    throw new Error('Invalid location.');
  }
  return normalized;
}

/**
 * Reverse geocode via OpenStreetMap Nominatim (best-effort).
 * @param {number} latitude
 * @param {number} longitude
 * @returns {Promise<string | null>}
 */
export async function reverseGeocodeLabel(latitude, longitude) {
  try {
    const url = new URL('https://nominatim.openstreetmap.org/reverse');
    url.searchParams.set('format', 'json');
    url.searchParams.set('lat', String(latitude));
    url.searchParams.set('lon', String(longitude));
    url.searchParams.set('zoom', '10');
    url.searchParams.set('addressdetails', '1');

    const res = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    const address = data?.address;
    if (!address || typeof address !== 'object') return null;

    const city =
      address.city ||
      address.town ||
      address.village ||
      address.suburb ||
      address.county ||
      address.state;
    const country = address.country;
    if (city && country) return `${city}, ${country}`;
    if (city) return String(city);
    if (typeof data.display_name === 'string') return data.display_name.split(',').slice(0, 2).join(',').trim();
    return null;
  } catch {
    return null;
  }
}

/** @param {number} latitude @param {number} longitude */
export function formatCoordinateLabel(latitude, longitude) {
  return `${latitude.toFixed(4)}°, ${longitude.toFixed(4)}°`;
}
